export const SCHEMA_VERSION = 6 as const;
export const BACKUP_FORMAT_VERSION = 3 as const;
export const MAX_CONTAINERS = 5_000;
export const MAX_EVENTS = 25_000;
export const MAX_TEMPERATURE_READINGS = 10_000;
export const MAX_TEMPERATURE_INCIDENTS = 2_000;
export const MAX_INCIDENT_RESOLUTIONS = 10_000;
export const MAX_RECIPE_SHOPPING_LISTS = 20;
export const MAX_RECIPE_SHOPPING_LIST_ITEMS = 50;
export const MAX_IMPORT_BYTES = 5 * 1024 * 1024;

export type Id = string;
export type IsoDate = string;
export type IsoDateTime = string;

export type QuantityUnit =
  | 'g'
  | 'kg'
  | 'ml'
  | 'l'
  | 'portion'
  | 'piece'
  | 'contenant';

export type ManufacturerDateType = 'none' | 'DLC' | 'DDM';
export type ContainerStatus = 'draft' | 'present' | 'partial' | 'out' | 'discarded' | 'archived';
export type IncidentStatus = 'none' | 'unresolved' | 'resolved';
export type AuditFlag = 'ok' | 'missing' | 'to_confirm';
export type NumberAssignmentKind = 'first' | 'reused' | 'restored' | 'reassigned' | 'legacy';
export type RotationAlertThreshold = 'j-7' | 'j-2' | 'j' | 'overdue';
export type RotationAlertState = 'unread' | 'read' | 'handled';
export type TimeCertainty = 'known' | 'estimated' | 'unknown';
export type FoodDateCertainty = Extract<TimeCertainty, 'known' | 'estimated'>;
export type FoodDateSource = 'household' | 'packaging';

export interface FoodDateEvidence {
  source: FoodDateSource;
  certainty: FoodDateCertainty;
}

export interface FoodDateEvidenceSet {
  storageStartedOn: FoodDateEvidence;
  purchasedOn: FoodDateEvidence | null;
  openedOn: FoodDateEvidence | null;
  useFirstOn: FoodDateEvidence | null;
  manufacturerDate: FoodDateEvidence | null;
}
export type TemperatureIncidentSource =
  | 'manual_observation'
  | 'mechanical_min_max'
  | 'freezer_alarm'
  | 'known_power_outage'
  | 'sensor_history';
export type TemperatureIncidentKind =
  | 'warming'
  | 'power_outage'
  | 'door_open'
  | 'equipment_failure'
  | 'unknown';
export type TemperatureIncidentStatus = 'open' | 'partially_resolved' | 'resolved';
export type IncidentResolutionChoice = 'cleared' | 'keep_reviewing';
export type EquipmentKind = 'three-star' | 'four-star' | 'fridge' | 'pantry' | 'unknown';
export type ContainerTrackingMode = 'visible-number' | 'internal-only';

export interface RotationAlertReceipt {
  threshold: RotationAlertThreshold;
  triggeredOn: IsoDate;
  createdAt: IsoDateTime;
  state: RotationAlertState;
}

export interface Household {
  id: Id;
  name: string;
  mode: 'local';
  locale: 'fr-FR';
  timezone: string;
  createdAt: IsoDateTime;
}

export interface StorageAppliance {
  id: Id;
  householdId: Id;
  name: string;
  equipmentKind: EquipmentKind;
  declaredTemperatureC: number | null;
  archivedAt: IsoDateTime | null;
  createdAt: IsoDateTime;
}

/**
 * Nom de type historique conservé dans les contrats de persistance.
 * L’interface représente désormais tout appareil froid pris en charge.
 */
export type Freezer = StorageAppliance;

export interface TemperatureTimeBoundary {
  value: IsoDateTime | null;
  certainty: TimeCertainty;
}

export interface TemperatureReading {
  id: Id;
  householdId: Id;
  freezerId: Id;
  deviceLabel: string;
  recordedAt: IsoDateTime;
  currentTemperatureC: number | null;
  minimumTemperatureC: number | null;
  maximumTemperatureC: number;
  unit: 'C';
  quality: 'manual';
  provenance: 'mechanical_min_max';
  resetOn: IsoDate | null;
  resetCertainty: TimeCertainty;
  note: string;
  idempotencyKey: Id;
}

export interface TemperatureIncident {
  id: Id;
  idempotencyKey: Id;
  householdId: Id;
  freezerId: Id;
  kind: TemperatureIncidentKind;
  source: TemperatureIncidentSource;
  started: TemperatureTimeBoundary;
  ended: TemperatureTimeBoundary;
  maximumTemperatureC: number | null;
  note: string;
  affectedContainerIds: Id[];
  status: TemperatureIncidentStatus;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export interface IncidentResolution {
  id: Id;
  idempotencyKey: Id;
  householdId: Id;
  incidentId: Id;
  containerId: Id;
  authorLocal: string;
  decidedAt: IsoDateTime;
  choice: IncidentResolutionChoice;
  comment: string;
}

export interface StorageNode {
  id: Id;
  freezerId: Id;
  parentId: Id | null;
  name: string;
  position: number;
  archivedAt: IsoDateTime | null;
}

export interface HorizonRuleSnapshot {
  id: Id;
  label: string;
  durationDays: number | null;
  sourceTitle: string;
  sourceUrl: string | null;
  sourceCheckedOn: IsoDate;
  version: string;
  disclaimer: string;
}

export interface FoodContainer {
  id: Id;
  householdId: Id;
  trackingMode: ContainerTrackingMode;
  serialNumber: number | null;
  numberAssignedAt: IsoDateTime | null;
  numberReleasedAt: IsoDateTime | null;
  numberAssignmentKind: NumberAssignmentKind | null;
  numberPolicyVersion: string | null;
  rotationAlerts: RotationAlertReceipt[];
  name: string;
  /** GTIN saisi ou détecté localement ; absent des sauvegardes schéma 6 antérieures. */
  barcode?: string | null | undefined;
  categoryId: Id;
  locationId: Id;
  initialQuantity: number | null;
  remainingQuantity: number | null;
  unit: QuantityUnit | null;
  storageStartedOn: IsoDate;
  /** Source et degré de certitude déclarés, sans interprétation sanitaire. */
  dateEvidence?: FoodDateEvidenceSet | undefined;
  /** Champs frigo optionnels pour préserver la lecture des sauvegardes schéma 6 antérieures. */
  purchasedOn?: IsoDate | null | undefined;
  openedOn?: IsoDate | null | undefined;
  useFirstOn?: IsoDate | null | undefined;
  manufacturerDateType: ManufacturerDateType;
  manufacturerDate: IsoDate | null;
  qualityHorizonOn: IsoDate | null;
  horizonRule: HorizonRuleSnapshot | null;
  status: ContainerStatus;
  incidentStatus: IncidentStatus;
  incidentNote: string;
  auditFlag: AuditFlag;
  notes: string;
  version: number;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export type InventoryEventType =
  | 'created_draft'
  | 'confirmed'
  | 'consumed_total'
  | 'consumed_partial'
  | 'moved'
  | 'discarded'
  | 'restored'
  | 'corrected'
  | 'incident_changed'
  | 'audit_missing'
  | 'audit_confirmed'
  | 'undo';

export interface EventPayload {
  before: FoodContainer | null;
  after: FoodContainer | null;
  summary: string;
  reason: string | null;
}

export interface InventoryEvent {
  id: Id;
  idempotencyKey: Id;
  householdId: Id;
  containerId: Id | null;
  eventType: InventoryEventType;
  payload: EventPayload;
  occurredAt: IsoDateTime;
  deviceId: Id;
  deviceSequence: number;
  revertsEventId: Id | null;
}

export interface AuditRecord {
  id: Id;
  householdId: Id;
  freezerId: Id;
  locationId: Id;
  expectedCycleIds: Id[];
  seenCycleIds: Id[];
  missingCycleIds: Id[];
  expectedSerials: number[];
  seenSerials: number[];
  missingSerials: number[];
  unknownSerials: number[];
  completedAt: IsoDateTime;
}

export interface RecipeShoppingListItem {
  id: Id;
  kind: 'stock' | 'pantry';
  ingredientId: Id;
  label: string;
  requiredQuantity: number | null;
  unit: QuantityUnit | null;
  checked: boolean;
}

export interface RecipeShoppingListHandoffReceipt {
  target: 'archibald' | 'paniprix';
  intentId: Id;
  status: 'accepted';
  signatureVerified: true;
  sourceMayRelease: true;
  verifiedAt: IsoDateTime;
}

export interface RecipeShoppingList {
  id: Id;
  householdId: Id;
  publicRecipeId: Id;
  recipeTitle: string;
  locale: 'fr-FR' | 'en';
  portions: number;
  sourceReturnDigest: string;
  items: RecipeShoppingListItem[];
  /** Preuve locale durable : la liste ne peut être libérée qu'après cet accusé vérifié. */
  handoffReceipt?: RecipeShoppingListHandoffReceipt | undefined;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export interface HouseholdPreferences {
  householdId: Id;
  exclusions: string[];
  safetyAcknowledged: boolean;
  /** Compatibilité sauvegardes V1/V2 ; migré à 7 et non utilisé par le calcul v1.1. */
  alertHorizonDays: number;
  lastLocationId: Id | null;
  /** Rappel local ou Web Push générique, sans donnée d’inventaire transmise au serveur. */
  reminderFrequency?: 'off' | 'daily' | 'weekly' | undefined;
  reminderRecipient?: 'in-app' | 'this-device' | undefined;
  lastReminderOn?: IsoDate | null | undefined;
  /** Listes locales conservées tant qu’ARCHIBALD n’a pas confirmé un import. */
  recipeShoppingLists?: RecipeShoppingList[] | undefined;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export interface AppMeta {
  key: 'app';
  schemaVersion: number;
  householdId: Id | null;
  deviceId: Id;
  deviceSequence: number;
  /** Borne haute historique conservée pour compatibilité et futures plages réservées. */
  nextSerial: number;
  persistentStorage: 'unknown' | 'granted' | 'denied' | 'unsupported';
  /** Présent uniquement pour le jeu d'essai local, jamais pour les données du foyer. */
  demoSeededAt?: IsoDateTime | null | undefined;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export interface HouseholdDeletionReceipt {
  householdId: Id;
  deletedAt: IsoDateTime;
}

export interface HouseholdDeletionResult {
  householdId: Id;
  deleted: boolean;
}

export interface InventorySnapshot {
  meta: AppMeta;
  household: Household | null;
  freezers: Freezer[];
  locations: StorageNode[];
  containers: FoodContainer[];
  events: InventoryEvent[];
  audits: AuditRecord[];
  temperatureReadings: TemperatureReading[];
  temperatureIncidents: TemperatureIncident[];
  incidentResolutions: IncidentResolution[];
  preferences: HouseholdPreferences | null;
}

export interface RecordTemperatureReadingInput {
  freezerId: Id;
  deviceLabel: string;
  currentTemperatureC: number | null;
  minimumTemperatureC: number | null;
  maximumTemperatureC: number;
  resetOn: IsoDate | null;
  resetCertainty: TimeCertainty;
  note: string;
}

export interface DeclareTemperatureIncidentInput {
  freezerId: Id;
  kind: TemperatureIncidentKind;
  source: TemperatureIncidentSource;
  started: TemperatureTimeBoundary;
  ended: TemperatureTimeBoundary;
  maximumTemperatureC: number | null;
  note: string;
}

export interface ResolveIncidentExposureInput {
  incidentId: Id;
  containerId: Id;
  authorLocal: string;
  choice: IncidentResolutionChoice;
  comment: string;
}

export interface InventoryMutation {
  container: FoodContainer;
  event: InventoryEvent;
}

export interface InventoryBatchMutation {
  idempotencyKey: Id;
  mutations: InventoryMutation[];
}

export interface CreateContainerInput {
  name: string;
  barcode?: string | null | undefined;
  categoryId: Id;
  locationId: Id;
  quantity: number | null;
  unit: QuantityUnit | null;
  storageStartedOn: IsoDate;
  dateEvidence?: FoodDateEvidenceSet | undefined;
  purchasedOn?: IsoDate | null | undefined;
  openedOn?: IsoDate | null | undefined;
  useFirstOn?: IsoDate | null | undefined;
  manufacturerDateType: ManufacturerDateType;
  manufacturerDate: IsoDate | null;
  horizonRule: HorizonRuleSnapshot | null;
  qualityHorizonOn: IsoDate | null;
  notes: string;
}

export interface SetupInput {
  householdName: string;
  freezerName: string;
  equipmentKind: Freezer['equipmentKind'];
  locationNames: string[];
  safetyAcknowledged: boolean;
  timezone: string;
}

export interface BackupEnvelope {
  format: 'inventaire-congelateurs';
  formatVersion: typeof BACKUP_FORMAT_VERSION;
  schemaVersion: number;
  exportedAt: IsoDateTime;
  appVersion: string;
  checksum: string;
  data: InventorySnapshot;
}

export const UNIT_LABELS: Record<QuantityUnit, string> = {
  g: 'g',
  kg: 'kg',
  ml: 'ml',
  l: 'l',
  portion: 'portion(s)',
  piece: 'pièce(s)',
  contenant: 'contenant(s)',
};
